import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, ExternalLink, AlertTriangle, Archive } from 'lucide-react';
import { BackupsPanel } from './BackupsPanel';
import { UnmatchedNamesFixer } from './UnmatchedNamesFixer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/** Slim bar above a day tab: sheet status, Read sheet now, Open sheet, month-end backups. */
export const SheetTools = ({ canEdit, monthKey, onSynced }) => {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [showFixer, setShowFixer] = useState(false);
  const load = useCallback(async () => {
    try { const s = await axios.get(`${API}/manpower/status`, auth()); setStatus(s.data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const sync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/manpower/sync`, {}, { ...auth(), timeout: 180000 });
      toast.success(`Sheet read — ${r.data.days} days, ${r.data.jobs} jobs${r.data.unmatched?.length ? ` · ${r.data.unmatched.length} unmatched name${r.data.unmatched.length !== 1 ? 's' : ''}` : ''}`);
      await load(); onSynced?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    setSyncing(false);
  };
  const unmatched = status?.last_summary?.unmatched || [];
  const missingWos = [...new Set((status?.last_summary?.schedule?.skipped || []).filter(x => /no project/i.test(x.reason || '')).map(x => x.work_order))];
  return (
    <div className="mb-3" data-testid="sheet-tools">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span data-testid="sheet-tools-status">
          Blair's Google sheet{status?.last_sync_at ? ` · last read ${new Date(status.last_sync_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} (${status.last_trigger || 'auto'})` : ''}
          {status?.headcount ? ` · Current Manpower ${status.headcount}` : ''} · wins on any WO/day it covers
        </span>
        {unmatched.length > 0 && (
          <button onClick={() => setShowFixer(v => !v)} className={`inline-flex items-center gap-1 font-semibold rounded px-1.5 py-0.5 ${showFixer ? 'bg-red-700 text-white' : 'text-red-700 hover:bg-red-50'}`} title={`${unmatched.join(' · ')} — click to link them to the right people`} data-testid="sheet-tools-unmatched">
            <AlertTriangle className="w-3.5 h-3.5" /> {unmatched.length} sheet name{unmatched.length !== 1 ? 's' : ''} don't match a user · fix
          </button>
        )}
        {missingWos.length > 0 && (
          <span className="inline-flex items-center gap-1 text-red-700 font-semibold" title={`These WO numbers are on the sheet but there is no work order with that number in Ops, so their numbers can't be shown on the schedule: ${missingWos.join(', ')}`} data-testid="sheet-tools-missing-wos">
            <AlertTriangle className="w-3.5 h-3.5" /> WO {missingWos.join(', ')} not in Ops
          </span>
        )}
        <span className="flex-1" />
        {status?.sheet_id && (
          <a href={`https://docs.google.com/spreadsheets/d/${status.sheet_id}/edit`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:text-slate-900 font-semibold" data-testid="mp-open-sheet">
            <ExternalLink className="w-3.5 h-3.5" /> Open sheet
          </a>
        )}
        {canEdit && (
          <button onClick={sync} disabled={syncing} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold" data-testid="mp-sync-now">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Reading…' : 'Read sheet now'}
          </button>
        )}
        <button onClick={() => setShowBackups(v => !v)} className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-lg border font-semibold ${showBackups ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 bg-white text-slate-700 hover:text-slate-900'}`} data-testid="sheet-tools-backups">
          <Archive className="w-3.5 h-3.5" /> Backups
        </button>
      </div>
      {showFixer && unmatched.length > 0 && <UnmatchedNamesFixer names={unmatched} canEdit={canEdit} onClose={() => setShowFixer(false)} onFixed={async () => { await load(); onSynced?.(); }} />}
      {showBackups && <div className="mt-2"><BackupsPanel monthKey={monthKey} defaultOpen /></div>}
    </div>
  );
};
